import React from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";

import CustomText from "../../../components/common/general/CustomText/CustomText";
import { ArrowSquareOutIcon } from "@phosphor-icons/react";
import { LimitedVisitSession } from "../../../graphql/generated/graphql";
import { formatDurationBetween } from "../../../utils/timeUtils";

interface UserSessionTableProps {
  sessions: LimitedVisitSession[];
  onSessionSelect?: (sessionId: string) => void;
}

const UserSessionTable: React.FC<UserSessionTableProps> = ({ sessions, onSessionSelect }) => {
  const handleViewSession = (sessionId: string) => {
    if (onSessionSelect) {
      onSessionSelect(sessionId);
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  return (
    <>
      <ScrollView style={styles.tableContainer} tabIndex={0}>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <View style={[styles.cell, styles.sessionIdCell]}>
              <CustomText text="Session ID" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.startDateTimeCell]}>
              <CustomText text="Start Date & Time" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.durationCell]}>
              <CustomText text="Duration" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.pageVisitsCell]}>
              <CustomText text="Page Visits" size="s" weight="medium" style={styles.headerText} />
            </View>
          </View>

          {sessions.map((session) => (
            <View key={session.id} style={styles.tableRow}>
              <View style={[styles.cell, styles.sessionIdCell]}>
                <View style={styles.sessionIdContent}>
                  <View style={styles.sessionInfo}>
                    {onSessionSelect ? (
                      <TouchableOpacity style={styles.sessionIdDetails} onPress={() => handleViewSession(session.id)}>
                        <CustomText
                          text={session.id}
                          weight="medium"
                          style={styles.sessionId}
                          useTranslationText={false}
                        />
                        <ArrowSquareOutIcon size={16} color="#3E68FE" />
                      </TouchableOpacity>
                    ) : (
                      <View style={styles.sessionIdDetails}>
                        <CustomText
                          text={session.id}
                          weight="medium"
                          style={[styles.sessionId, { cursor: "default" }]}
                          useTranslationText={false}
                        />
                      </View>
                    )}
                    <CustomText
                      text={session.ended ? "Ended" : "Active"}
                      size="xs"
                      style={[styles.sessionStatus, { color: session.ended ? "#6B7280" : "#10B981" }]}
                      useTranslationText={false}
                    />
                  </View>
                </View>
              </View>

              <View style={[styles.cell, styles.startDateTimeCell]}>
                <CustomText
                  text={formatDateTime(session.startTime)}
                  size="s"
                  style={styles.startDateTimeText}
                  useTranslationText={false}
                />
              </View>

              <View style={[styles.cell, styles.durationCell]}>
                <CustomText
                  text={formatDurationBetween(session.startTime, session.lastActiveTime)}
                  size="s"
                  style={styles.durationText}
                  useTranslationText={false}
                />
              </View>

              <View style={[styles.cell, styles.pageVisitsCell]}>
                <CustomText
                  text={session.pageVisitsCount?.toString() || "0"}
                  size="s"
                  style={styles.pageVisitsText}
                  useTranslationText={false}
                />
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  tableContainer: {
    flex: 1,
  },
  table: {
    backgroundColor: "#fff",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 72,
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  sessionIdCell: {
    flex: 3,
  },
  startDateTimeCell: {
    flex: 2,
    alignItems: "flex-start",
  },
  durationCell: {
    flex: 1,
    alignItems: "center",
  },
  pageVisitsCell: {
    flex: 1,
    alignItems: "center",
  },
  headerText: {
    color: "#6B7280",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  sessionIdContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  sessionInfo: {
    flexDirection: "column",
    gap: 4,
  },
  sessionIdDetails: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  sessionId: {
    color: "#3E68FE",
    fontSize: 15,
    cursor: "pointer",
  },
  sessionStatus: {
    fontSize: 12,
  },
  startDateTimeText: {
    color: "#6B7280",
  },
  durationText: {
    color: "#374151",
    fontWeight: "500",
  },
  pageVisitsText: {
    color: "#374151",
    fontWeight: "500",
  },
});

export default UserSessionTable;
